import { useState, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { AppProvider } from './contexts/AppContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { CartProvider } from './apps/shop/contexts/CartContext'
import Header from './components/Header'
import Navigation from './components/Navigation'
import Login from './components/Login'
import Register from './components/Register'
import AdminApps from './pages/AdminApps'
import ProtectedRoute from './components/ProtectedRoute'
import PublicRoute from './components/PublicRoute'
import LoadingOverlay from './components/LoadingOverlay'
import SessionTimeoutWarning from './components/SessionTimeoutWarning'
import SessionExpired from './pages/SessionExpired'
import Unauthorized from './pages/Unauthorized'
import { useUserActivity } from './hooks/useUserActivity'
import { getAppExports, getDefaultApp, getAvailableApps } from './apps'
import apiService from './services/apiService'
import ROSidebar from './apps/routeoptimizer/components/ROSidebar'
import { DEPLOYMENT_CONFIG } from '../deployment.config'
import './styles/theme.css'
import './App.css'

// Main App Content (inside AuthProvider)
function AppContent() {
  const [loading, setLoading] = useState(true)
  const [activeApp, setActiveApp] = useState(null)
  const [deploymentInfo, setDeploymentInfo] = useState(null)
  const { logout, isAuthenticated, isInitialized } = useAuth()

  // HIPAA: 15-minute session timeout with 2-minute warning (disabled for Route Optimizer, Heston, and Shah)
  const isDemoMode = activeApp === 'routeoptimizer' || activeApp === 'heston' || activeApp === 'shah'
  const { showWarning, minutesRemaining, secondsRemaining, extendSession } = useUserActivity({ disabled: isDemoMode })

  // Proactive token refresh — refresh access token every 13 min (before 15-min expiry)
  // Prevents 401 errors and session loss, especially important for Route Optimizer
  useEffect(() => {
    if (!isAuthenticated) return
    const REFRESH_INTERVAL = 13 * 60 * 1000 // 13 minutes
    const refreshAccessToken = async () => {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) return
      try {
        const resp = await apiService.post('/auth/refresh', { refreshToken }, { timeout: 15000 })
        if (resp.accessToken) {
          localStorage.setItem('accessToken', resp.accessToken)
          if (resp.csrfToken) localStorage.setItem('csrfToken', resp.csrfToken)
          console.log('[App] Access token proactively refreshed')
        }
      } catch (err) {
        console.warn('[App] Proactive token refresh failed:', err.message)
      }
    }
    const id = setInterval(refreshAccessToken, REFRESH_INTERVAL)
    return () => clearInterval(id)
  }, [isAuthenticated])

  useEffect(() => {
    // Wait for AuthContext to finish initializing before proceeding
    if (!isInitialized) {
      return; // Don't do anything until auth initialization completes
    }

    // Initialize app system
    const initializeApp = async () => {
      try {
        // Fetch deployment info to determine if singleapp or multiapp
        if (isAuthenticated) {
          try {
            const dashboardData = await apiService.get('/api/dashboard')
            setDeploymentInfo(dashboardData.deployment)

            // Set active app based on deployment type
            const isSingleApp = dashboardData.deployment?.type === 'singleapp-private' || dashboardData.deployment?.type === 'singleapp-public';
            if (isSingleApp && dashboardData.deployment?.defaultApp) {
              setActiveApp(dashboardData.deployment.defaultApp)
            } else {
              const defaultAppId = getDefaultApp()
              setActiveApp(defaultAppId)
            }

            // CRITICAL: Only set loading to false AFTER deployment info is fetched
            // This prevents race condition where Navigate executes before deploymentInfo is set
            setLoading(false)
          } catch (error) {
            // Expected 404 in single-app deployments - use build-time config
            if (error?.response?.status !== 404) {
              console.error('Error fetching deployment info:', error)
            }
            // Fallback to build-time deployment config
            const fallbackDeployment = {
              type: DEPLOYMENT_CONFIG.type,
              defaultApp: DEPLOYMENT_CONFIG.defaultApp
            }
            setDeploymentInfo(fallbackDeployment)

            // Set activeApp based on deployment type (same logic as non-authenticated path)
            const isSingleApp = fallbackDeployment.type === 'singleapp-private' || fallbackDeployment.type === 'singleapp-public';
            if (isSingleApp && fallbackDeployment.defaultApp) {
              setActiveApp(fallbackDeployment.defaultApp)
            } else {
              setActiveApp(getDefaultApp())
            }
            setLoading(false)
          }
        } else {
          // Not authenticated - use build-time deployment config
          const deploymentData = {
            type: DEPLOYMENT_CONFIG.type,
            defaultApp: DEPLOYMENT_CONFIG.defaultApp
          }
          setDeploymentInfo(deploymentData)

          // Set activeApp based on deployment type (for proper redirects)
          const isSingleApp = deploymentData.type === 'singleapp-private' || deploymentData.type === 'singleapp-public';
          if (isSingleApp && deploymentData.defaultApp) {
            setActiveApp(deploymentData.defaultApp)
          }
          setLoading(false)
        }
      } catch (error) {
        console.error('Error initializing app:', error)
        setLoading(false)
      }
    }

    initializeApp()
  }, [isAuthenticated, isInitialized])

  // Generate routes for all available apps
  const generateAppRoutes = () => {
    const routes = []

    // Add admin routes (only for multiapp deployments)
    if (deploymentInfo?.type !== 'singleapp') {
      routes.push(
        <Route
          key="admin-apps"
          path="/admin/apps"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminApps />
            </ProtectedRoute>
          }
        />
      )
    }

    // Add routes for apps (filtered by deployment type)
    const availableApps = getAvailableApps()
    availableApps.forEach(app => {
      // In singleapp mode, ONLY create routes for the defaultApp + hidden apps (jobs-monitor)
      const hiddenApps = ['jobs-monitor']; // Apps accessible in single-app mode but not shown in UI
      const isHiddenApp = hiddenApps.includes(app.id);

      const isSingleApp = deploymentInfo?.type === 'singleapp-private' || deploymentInfo?.type === 'singleapp-public';
      if (isSingleApp && app.id !== deploymentInfo?.defaultApp && !isHiddenApp) {
        return; // Skip this app entirely
      }

      const appExports = getAppExports(app.id)
      if (appExports) {
        // Special routing for shop app (public + protected routes)
        if (app.id === 'shop') {
          // Wrap all shop routes in CartProvider
          // Public routes (no auth required)
          if (appExports.Home) {
            routes.push(
              <Route key="shop-home" path="/shop" element={<CartProvider><PublicRoute><appExports.Home /></PublicRoute></CartProvider>} />
            )
          }
          if (appExports.ProductCatalog) {
            routes.push(
              <Route key="shop-catalog" path="/shop/catalog" element={<CartProvider><PublicRoute><appExports.ProductCatalog /></PublicRoute></CartProvider>} />
            )
          }
          if (appExports.ProductDetail) {
            routes.push(
              <Route key="shop-product" path="/shop/product/:id" element={<CartProvider><PublicRoute><appExports.ProductDetail /></PublicRoute></CartProvider>} />
            )
          }
          if (appExports.Cart) {
            routes.push(
              <Route key="shop-cart" path="/shop/cart" element={<CartProvider><PublicRoute><appExports.Cart /></PublicRoute></CartProvider>} />
            )
          }
          if (appExports.Search) {
            routes.push(
              <Route key="shop-search" path="/shop/search" element={<CartProvider><PublicRoute><appExports.Search /></PublicRoute></CartProvider>} />
            )
          }
          if (appExports.Wishlist) {
            routes.push(
              <Route key="shop-wishlist" path="/shop/wishlist" element={<CartProvider><PublicRoute><appExports.Wishlist /></PublicRoute></CartProvider>} />
            )
          }

          // Protected routes (auth required)
          if (appExports.Checkout) {
            routes.push(
              <Route key="shop-checkout" path="/shop/checkout" element={<CartProvider><ProtectedRoute><appExports.Checkout /></ProtectedRoute></CartProvider>} />
            )
          }
          if (appExports.OrderHistory) {
            routes.push(
              <Route key="shop-orders" path="/shop/orders" element={<CartProvider><ProtectedRoute><appExports.OrderHistory /></ProtectedRoute></CartProvider>} />
            )
          }
          if (appExports.OrderDetail) {
            routes.push(
              <Route key="shop-order" path="/shop/order/:id" element={<CartProvider><ProtectedRoute><appExports.OrderDetail /></ProtectedRoute></CartProvider>} />
            )
          }
          if (appExports.Account) {
            routes.push(
              <Route key="shop-account" path="/shop/account" element={<CartProvider><ProtectedRoute><appExports.Account /></ProtectedRoute></CartProvider>} />
            )
          }

          // Admin routes (admin role required)
          if (appExports.ShopAdmin) {
            routes.push(
              <Route key="shop-admin" path="/shop/admin" element={<CartProvider><ProtectedRoute requireAdmin={true}><appExports.ShopAdmin /></ProtectedRoute></CartProvider>} />
            )
          }
        } else if (app.id === 'certus') {
          // Custom routing for CERTUS app
          if (appExports.Dashboard) {
            routes.push(
              <Route
                key="certus-dashboard"
                path="/certus"
                element={
                  <ProtectedRoute>
                    <appExports.Dashboard />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.ProjectDetailPage) {
            routes.push(
              <Route
                key="certus-project-detail"
                path="/certus/projects/:projectId"
                element={
                  <ProtectedRoute>
                    <appExports.ProjectDetailPage />
                  </ProtectedRoute>
                }
              />
            )
          }
        } else if (app.id === 'heston') {
          // Custom routing for HESTON app (Certus clone - pricing simulator)
          if (appExports.Dashboard) {
            routes.push(
              <Route
                key="heston-dashboard"
                path="/heston"
                element={
                  <ProtectedRoute>
                    <appExports.Dashboard />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.ProjectDetailPage) {
            routes.push(
              <Route
                key="heston-project-detail"
                path="/heston/projects/:projectId"
                element={
                  <ProtectedRoute>
                    <appExports.ProjectDetailPage />
                  </ProtectedRoute>
                }
              />
            )
            // Redirect legacy analytics to main dashboard
            routes.push(
              <Route
                key="heston-project-analytics-redirect"
                path="/heston/projects/:projectId/analytics"
                element={
                  <ProtectedRoute>
                    <appExports.ProjectDetailPage />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.ProjectPricesPage) {
            routes.push(
              <Route
                key="heston-project-prices"
                path="/heston/projects/:projectId/prices"
                element={
                  <ProtectedRoute>
                    <appExports.ProjectPricesPage />
                  </ProtectedRoute>
                }
              />
            )
          }
        } else if (app.id === 'clonetest') {
          // Custom routing for CLONETEST app
          if (appExports.Dashboard) {
            routes.push(
              <Route
                key="clonetest-dashboard"
                path="/clonetest"
                element={
                  <ProtectedRoute>
                    <appExports.Dashboard />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.ProjectDetailPage) {
            routes.push(
              <Route
                key="clonetest-project-detail"
                path="/clonetest/projects/:projectId"
                element={
                  <ProtectedRoute>
                    <appExports.ProjectDetailPage />
                  </ProtectedRoute>
                }
              />
            )
          }
        } else if (app.id === 'playground') {
          // Custom routing for PLAYGROUND app
          if (appExports.Dashboard) {
            routes.push(
              <Route
                key="playground-dashboard"
                path="/playground"
                element={
                  <ProtectedRoute>
                    <appExports.Dashboard />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.ProjectDetailPage) {
            routes.push(
              <Route
                key="playground-project-detail"
                path="/playground/projects/:projectId"
                element={
                  <ProtectedRoute>
                    <appExports.ProjectDetailPage />
                  </ProtectedRoute>
                }
              />
            )
          }
        } else if (app.id === 'project750k') {
          // Custom routing for PROJECT 750K app
          if (appExports.Dashboard) {
            routes.push(
              <Route
                key="project750k-dashboard"
                path="/project750k"
                element={
                  <ProtectedRoute>
                    <appExports.Dashboard />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.UccDataViewer) {
            routes.push(
              <Route
                key="project750k-ucc-data"
                path="/ucc-data"
                element={
                  <ProtectedRoute>
                    <appExports.UccDataViewer />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.UccDataViewerListC) {
            routes.push(
              <Route
                key="project750k-ucc-data-listc"
                path="/ucc-data-listc"
                element={
                  <ProtectedRoute>
                    <appExports.UccDataViewerListC />
                  </ProtectedRoute>
                }
              />
            )
          }

          // Report routes
          if (appExports.DebtorLookup) {
            routes.push(
              <Route
                key="project750k-debtor-lookup"
                path="/reports/debtor-lookup"
                element={
                  <ProtectedRoute>
                    <appExports.DebtorLookup />
                  </ProtectedRoute>
                }
              />
            )
          }

          if (appExports.RegionalAnalysis) {
            routes.push(
              <Route
                key="project750k-regional-analysis"
                path="/reports/regional-analysis"
                element={
                  <ProtectedRoute>
                    <appExports.RegionalAnalysis />
                  </ProtectedRoute>
                }
              />
            )
          }

          if (appExports.TopDebtors) {
            routes.push(
              <Route
                key="project750k-top-debtors"
                path="/reports/top-debtors"
                element={
                  <ProtectedRoute>
                    <appExports.TopDebtors />
                  </ProtectedRoute>
                }
              />
            )
          }

          if (appExports.SecuredPartyAnalysis) {
            routes.push(
              <Route
                key="project750k-secured-party-analysis"
                path="/reports/secured-parties"
                element={
                  <ProtectedRoute>
                    <appExports.SecuredPartyAnalysis />
                  </ProtectedRoute>
                }
              />
            )
          }

          if (appExports.AmendmentTrends) {
            routes.push(
              <Route
                key="project750k-amendment-trends"
                path="/reports/amendments"
                element={
                  <ProtectedRoute>
                    <appExports.AmendmentTrends />
                  </ProtectedRoute>
                }
              />
            )
          }
        } else if (app.id === 'routeoptimizer') {
          // Custom routing for Route Optimizer app
          if (appExports.Dashboard) {
            routes.push(
              <Route
                key="routeoptimizer-dashboard"
                path="/routeoptimizer"
                element={
                  <ProtectedRoute>
                    <appExports.Dashboard />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.CompanyDetailPage) {
            routes.push(
              <Route
                key="routeoptimizer-company"
                path="/routeoptimizer/company/:companyId"
                element={
                  <ProtectedRoute>
                    <appExports.CompanyDetailPage />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.BranchDetailPage) {
            routes.push(
              <Route
                key="routeoptimizer-branch"
                path="/routeoptimizer/branch/:branchId"
                element={
                  <ProtectedRoute>
                    <appExports.BranchDetailPage />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.WeekDetailPage) {
            routes.push(
              <Route
                key="routeoptimizer-week"
                path="/routeoptimizer/week/:weekId"
                element={
                  <ProtectedRoute>
                    <appExports.WeekDetailPage />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.AdminPage) {
            routes.push(
              <Route
                key="routeoptimizer-admin"
                path="/routeoptimizer/admin"
                element={
                  <ProtectedRoute>
                    <appExports.AdminPage />
                  </ProtectedRoute>
                }
              />
            )
          }
        } else if (app.id === 'marketing') {
          // Custom routing for Marketing app
          if (appExports.Dashboard) {
            routes.push(
              <Route
                key="marketing-dashboard"
                path="/marketing"
                element={
                  <ProtectedRoute>
                    <appExports.Dashboard />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.CreateCampaign) {
            routes.push(
              <Route
                key="marketing-create"
                path="/marketing/create"
                element={
                  <ProtectedRoute>
                    <appExports.CreateCampaign />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.CampaignDetail) {
            routes.push(
              <Route
                key="marketing-detail"
                path="/marketing/:campaignId"
                element={
                  <ProtectedRoute>
                    <appExports.CampaignDetail />
                  </ProtectedRoute>
                }
              />
            )
          }
          if (appExports.Analytics) {
            routes.push(
              <Route
                key="marketing-analytics"
                path="/marketing/analytics"
                element={
                  <ProtectedRoute>
                    <appExports.Analytics />
                  </ProtectedRoute>
                }
              />
            )
          }
        } else {
          // Standard app routing (Dashboard/Settings/Analytics)
          // Dashboard route
          if (appExports.Dashboard) {
            const isPublicShah = app.id === 'shah' && deploymentInfo?.type === 'singleapp-public';
            const Guard = isPublicShah ? PublicRoute : ProtectedRoute;
            routes.push(
              <Route
                key={`${app.id}-dashboard`}
                path={`/${app.id}`}
                element={
                  <Guard>
                    <appExports.Dashboard />
                  </Guard>
                }
              />
            )
          }

          // Settings route
          if (appExports.Settings) {
            routes.push(
              <Route
                key={`${app.id}-settings`}
                path={`/${app.id}/settings`}
                element={
                  <ProtectedRoute>
                    <appExports.Settings />
                  </ProtectedRoute>
                }
              />
            )
          }

          // Analytics route
          if (appExports.Analytics) {
            routes.push(
              <Route
                key={`${app.id}-analytics`}
                path={`/${app.id}/analytics`}
                element={
                  <ProtectedRoute>
                    <appExports.Analytics />
                  </ProtectedRoute>
                }
              />
            )
          }
        }
      }
    })

    return routes
  }

  // Don't render until we have deployment info (for authenticated users)
  if (loading || (isAuthenticated && !deploymentInfo)) {
    return <LoadingOverlay />
  }

  return (
    <ThemeProvider>
      <AppProvider activeApp={activeApp} setActiveApp={setActiveApp} deploymentInfo={deploymentInfo}>
        <div className="app">
          {/* HIPAA: Session timeout warning (shows 2 minutes before timeout) */}
          {isAuthenticated && showWarning && (
            <SessionTimeoutWarning
              showWarning={showWarning}
              minutesRemaining={minutesRemaining}
              secondsRemaining={secondsRemaining}
              onExtendSession={extendSession}
              onLogout={logout}
            />
          )}

          <Routes>
            {/* Public routes - standalone without Header/Navigation */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/hipaa-session-expiry" element={<SessionExpired />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Protected routes - with Header/Navigation layout or full-screen for SHAH */}
            <Route path="*" element={
              activeApp === 'shah' ? (
                <main className="main-content-shah">
                  <Routes>
                    {/* App-specific routes (all protected) */}
                    {generateAppRoutes()}

                    {/* Default redirect - smart routing based on deployment type */}
                    <Route
                      path="/"
                      element={<Navigate to="/shah" replace />}
                    />
                    <Route
                      path="/dashboard"
                      element={<Navigate to="/shah" replace />}
                    />
                  </Routes>
                </main>
              ) : (
                <>
                  <Header />
                  <div className="app-content">
                    <Navigation />
                    {activeApp === 'routeoptimizer' && <ROSidebar />}
                    <main className="main-content">
                      <Routes>
                        {/* App-specific routes (all protected) */}
                        {generateAppRoutes()}

                        {/* Default redirect - smart routing based on deployment type */}
                        <Route
                          path="/"
                          element={
                            <Navigate
                              to={
                                (deploymentInfo?.type === 'singleapp-private' || deploymentInfo?.type === 'singleapp-public') && deploymentInfo?.defaultApp
                                  ? `/${deploymentInfo.defaultApp}`
                                  : (activeApp ? `/${activeApp}` : (() => {
                                    // For multiapp, redirect to first available app instead of /admin/apps
                                    const availableApps = getAvailableApps();
                                    return availableApps.length > 0 ? `/${availableApps[0].id}` : '/login';
                                  })())
                              }
                              replace
                            />
                          }
                        />
                        <Route
                          path="/dashboard"
                          element={
                            <Navigate
                              to={
                                (deploymentInfo?.type === 'singleapp-private' || deploymentInfo?.type === 'singleapp-public') && deploymentInfo?.defaultApp
                                  ? `/${deploymentInfo.defaultApp}`
                                  : (activeApp ? `/${activeApp}` : (() => {
                                    // For multiapp, redirect to first available app instead of /admin/apps
                                    const availableApps = getAvailableApps();
                                    return availableApps.length > 0 ? `/${availableApps[0].id}` : '/login';
                                  })())
                              }
                              replace
                            />
                          }
                        />
                      </Routes>
                    </main>
                  </div>
                </>
              )
            } />
          </Routes>
        </div>
      </AppProvider>
    </ThemeProvider>
  )
}

// Root App Component (wraps with AuthProvider)
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
